import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';
import classes from './templates.module.scss';
import classes2 from '../files/styles.module.scss';
import { createCn } from '../../helpers/utils';
import {
  ProtocolTemplate,
  ProtocolTemplatePayload,
  templatesService,
} from '../../services/templates.service';
import TemplateCard from './components/template-card.component';
import TemplateForm from './components/template-form.component';
import TemplateImport from './components/template-import.component';

const cn = createCn('templates', classes);
const cn2 = createCn('files', classes2);

const TemplatesPage = () => {
  const [templates, setTemplates] = useState<ProtocolTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProtocolTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await templatesService.list();
      setTemplates(data);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить шаблоны');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateForm = () => {
    setEditingTemplate(null);
    setFormOpen(true);
  };

  const openEditForm = (template: ProtocolTemplate) => {
    setEditingTemplate(template);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingTemplate(null);
  };

  const handleFormSubmit = async (payload: ProtocolTemplatePayload) => {
    setSaving(true);
    try {
      if (editingTemplate) {
        const updated = await templatesService.update(editingTemplate.id, payload);
        setTemplates((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        toast.success('Шаблон сохранён');
      } else {
        const created = await templatesService.create(payload);
        setTemplates((prev) => [...prev, created]);
        toast.success('Шаблон создан');
      }
      closeForm();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось сохранить шаблон');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template: ProtocolTemplate) => {
    if (template.is_default) return;
    if (!window.confirm(`Удалить шаблон «${template.name}»?`)) return;

    setDeletingId(template.id);
    try {
      await templatesService.remove(template.id);
      setTemplates((prev) => prev.filter((item) => item.id !== template.id));
      toast.success('Шаблон удалён');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось удалить шаблон');
    } finally {
      setDeletingId(null);
    }
  };

  const handleImportDocx = async (file: File): Promise<boolean> => {
    setImporting(true);
    try {
      const created = await templatesService.importDocx(file);
      setTemplates((prev) => [...prev, created]);
      toast.success(`Шаблон «${created.name}» импортирован из DOCX`);
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось импортировать шаблон из DOCX');
      return false;
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={cn2('')}>
      <div className={cn2('heading')}>Шаблоны</div>

      <div className={cn2('content')}>
        <p className={cn('subtitle')}>
          Библиотека шаблонов протоколов совещаний. Секции шаблона используются AI при генерации
          протокола — задайте заголовки и подсказки для каждой секции.
        </p>

        <TemplateImport importing={importing} onImport={handleImportDocx} />

        {loading ? (
          <div className={cn('status')}>Загрузка…</div>
        ) : (
          <>
            {loadError && (
              <div className={cn('error-banner')}>
                {loadError}
                <button type="button" onClick={load}>Повторить</button>
              </div>
            )}

            <div className={cn('toolbar')}>
              <div className={cn('section-title')}>Шаблоны ({templates.length})</div>
              <button type="button" className={cn('create-btn')} onClick={openCreateForm}>
                <Plus size={16} /> Создать шаблон
              </button>
            </div>

            {formOpen && (
              <TemplateForm
                template={editingTemplate}
                saving={saving}
                onSubmit={handleFormSubmit}
                onCancel={closeForm}
              />
            )}

            {templates.length === 0 ? (
              <div className={cn('empty')}>Шаблонов пока нет</div>
            ) : (
              <div className={cn('grid')}>
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onEdit={() => openEditForm(template)}
                    onDelete={() => handleDelete(template)}
                    deleting={deletingId === template.id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TemplatesPage;
